import React from "react";
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { activeUsersReport, activeUsersSummary, activeUsersReportByRange, activeUsersSummaryByRange } from "./graphql";
import type { ActiveUsersReportQuery } from "../../__generated__/graphql";

const metricPalette = ["#1e88e5", "#43a047"];

type ChartRow = Record<string, string | number>;
type ActiveUsersReport = NonNullable<ActiveUsersReportQuery["analyticsReport"]>;
type ActiveUsersRow = ActiveUsersReport["rows"][number];
type EnrichedRow = {
  normalizedDimensionValue: string;
  sortValue: number | string;
  metrics: Record<string, number>;
};
type MetricTotals = {
  activeUsersTotal: number;
  sessionsTotal: number;
};

interface ActiveUsersSummaryQuery {
  analyticsReport?: {
    metricHeaders: string[];
    rows: Array<{ metricValues: string[] }>;
  } | null;
}

const normalizeDimensionValue = (value: string) => {
  if (!value) return "";
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value;
};

const parseMetricValue = (value?: string | null) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const compareSortValues = (a: number | string, b: number | string) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

const findHeaderIndex = (headers: string[], target: string) =>
  headers.findIndex((header) => header.toLowerCase() === target.toLowerCase());

interface ActiveUsersCardProps {
  startDate?: string;
  endDate?: string;
}

export const ActiveUsersCard = ({ startDate, endDate }: ActiveUsersCardProps = {}) => {
  const hasRange = !!startDate && !!endDate;
  const rangeVars = hasRange ? { variables: { startDate, endDate } } : undefined;
  const { data, loading, error } = useQuery<ActiveUsersReportQuery>(
    hasRange ? activeUsersReportByRange : activeUsersReport,
    rangeVars
  );
  const { data: summaryData } = useQuery<ActiveUsersSummaryQuery>(
    hasRange ? activeUsersSummaryByRange : activeUsersSummary,
    rangeVars
  );
  const report = data?.analyticsReport ?? null;
  const summaryReport = summaryData?.analyticsReport ?? null;
  const dimensionKey = report?.dimensionHeaders?.[0] ?? "dimension";

  const chartData = React.useMemo<ChartRow[]>(() => {
    if (!report) return [];
    const enrichedRows = report.rows.map((row: ActiveUsersRow) => {
      const rawDimensionValue = row.dimensionValues[0] ?? "";
      const normalizedDimensionValue = normalizeDimensionValue(rawDimensionValue);
      const sortValue = /^\d{8}$/.test(rawDimensionValue)
        ? Number(rawDimensionValue)
        : normalizedDimensionValue;
      const metrics = report.metricHeaders.reduce<Record<string, number>>(
        (acc: Record<string, number>, header: string, index: number) => {
          acc[header] = parseMetricValue(row.metricValues[index]);
          return acc;
        },
        {} as Record<string, number>
      );

      return { normalizedDimensionValue, sortValue, metrics } as EnrichedRow;
    });

    return enrichedRows
      .sort((a: EnrichedRow, b: EnrichedRow) =>
        compareSortValues(a.sortValue, b.sortValue)
      )
      .map((item: EnrichedRow) => ({
        [dimensionKey]: item.normalizedDimensionValue,
        ...item.metrics,
      }));
  }, [report, dimensionKey]);

  const totals = React.useMemo<MetricTotals>(() => {
    if (!report) return { activeUsersTotal: 0, sessionsTotal: 0 };
    const activeUsersIndex = findHeaderIndex(report.metricHeaders, "activeUsers");
    const sessionsIndex = findHeaderIndex(report.metricHeaders, "sessions");

    const summaryTotals = (() => {
      if (!summaryReport || summaryReport.rows.length === 0) return null;
      const summaryActiveIndex = findHeaderIndex(
        summaryReport.metricHeaders,
        "activeUsers"
      );
      const summarySessionsIndex = findHeaderIndex(
        summaryReport.metricHeaders,
        "sessions"
      );
      const summaryRow = summaryReport.rows[0];
      return {
        activeUsersTotal:
          summaryActiveIndex >= 0
            ? parseMetricValue(summaryRow.metricValues[summaryActiveIndex])
            : 0,
        sessionsTotal:
          summarySessionsIndex >= 0
            ? parseMetricValue(summaryRow.metricValues[summarySessionsIndex])
            : 0,
      };
    })();

    if (summaryTotals) return summaryTotals;

    return report.rows.reduce<MetricTotals>(
      (acc, row) => {
        if (activeUsersIndex >= 0) {
          acc.activeUsersTotal += parseMetricValue(
            row.metricValues[activeUsersIndex]
          );
        }
        if (sessionsIndex >= 0) {
          acc.sessionsTotal += parseMetricValue(
            row.metricValues[sessionsIndex]
          );
        }
        return acc;
      },
      { activeUsersTotal: 0, sessionsTotal: 0 }
    );
  }, [report, summaryReport]);

  if (loading) {
    return (
      <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load chart.
      </Typography>
    );
  }

  if (!report) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Active Users (30 days)
          </Typography>
          <Typography variant="h6">{totals.activeUsersTotal}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Sessions (30 days)
          </Typography>
          <Typography variant="h6">{totals.sessionsTotal}</Typography>
        </Box>
      </Box>
      <Box sx={{ width: "100%", height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData.slice(-14)} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={dimensionKey} hide />
            <YAxis width={32} />
            <Tooltip />
            {report.metricHeaders.map((header: string, index: number) => (
              <Line
                key={header}
                type="monotone"
                dataKey={header}
                stroke={metricPalette[index % metricPalette.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
